import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, Area, AreaChart } from 'recharts';
import { chartConfig } from './config';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '~/components/ui/shadcn/components/ui/chart';
import { formatBytes, formatFullDate } from '~/utils/format';

export function formatDateTick(value: string) {
  const date = new Date(value);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export type VisitorsPoint = { date: string; visitors: number };
export function useVisitorsChart(visitors?: Array<{ start: string; end: string; count: number }>) {
  return useMemo(() => {
    const rows: VisitorsPoint[] = (visitors ?? []).map((v) => ({ date: v.start, visitors: v.count }));
    const total = rows.reduce((acc, r) => acc + (r.visitors || 0), 0);

    return { rows, total };
  }, [visitors]);
}

export type PageviewsPoint = { date: string; pageviews: number };
export function usePageviewsChart(pageviews?: Array<{ start: string; end: string; count: number }>) {
  return useMemo(() => {
    const rows: PageviewsPoint[] = (pageviews ?? []).map((p) => ({ date: p.start, pageviews: p.count }));
    const total = rows.reduce((acc, r) => acc + (r.pageviews || 0), 0);

    return { rows, total };
  }, [pageviews]);
}

export type BandwidthPoint = { date: string; bandwidth: number };
export function useBandwidthChart(bandwidth?: Array<{ start: string; end: string; bandwidth: number }>) {
  return useMemo(() => {
    const points: BandwidthPoint[] = (bandwidth ?? []).map((b) => ({ date: b.start, bandwidth: b.bandwidth }));
    const byDay = new Map<string, number>();

    for (const p of points) {
      const d = new Date(p.date);
      const key = new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
      byDay.set(key, (byDay.get(key) || 0) + (p.bandwidth || 0));
    }

    const rows: BandwidthPoint[] = Array.from(byDay.entries())
      .map(([key, value]) => ({ date: key, bandwidth: value }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const total = rows.reduce((acc, r) => acc + (r.bandwidth || 0), 0);

    return { rows, total };
  }, [bandwidth]);
}

export function VisitorsAreaChart({ data, gridStroke }: { data: VisitorsPoint[]; gridStroke: string }) {
  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
      <AreaChart data={data} margin={{ left: 0, right: 8 }}>
        <defs>
          <linearGradient id="visitorsFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-visitors)" stopOpacity={0.25} />
            <stop offset="100%" stopColor="var(--color-visitors)" stopOpacity={0.04} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke={gridStroke} />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={32}
          tickFormatter={formatDateTick}
        />
        <YAxis
          width={40}
          tick={{ stroke: '#828282', strokeWidth: 0.25 }}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickCount={5}
          allowDecimals={false}
          tickFormatter={(v: number) => v.toLocaleString()}
        />
        <ChartTooltip
          isAnimationActive={false}
          cursor={false}
          wrapperStyle={{ pointerEvents: 'none' }}
          content={
            <ChartTooltipContent
              className="w-[160px]"
              labelFormatter={(value) => formatFullDate(String(value))}
              indicator="dot"
            />
          }
        />
        <Area
          type="monotone"
          dataKey="visitors"
          stroke="var(--color-visitors)"
          strokeWidth={2}
          fill="url(#visitorsFill)"
          dot={false}
          activeDot={{ r: 3 }}
        />
      </AreaChart>
    </ChartContainer>
  );
}

export function PageviewsAreaChart({ data, gridStroke }: { data: PageviewsPoint[]; gridStroke: string }) {
  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
      <AreaChart data={data} margin={{ left: 0, right: 8 }}>
        <defs>
          <linearGradient id="pageviewsFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-pageviews)" stopOpacity={0.25} />
            <stop offset="100%" stopColor="var(--color-pageviews)" stopOpacity={0.04} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke={gridStroke} />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={32}
          tickFormatter={formatDateTick}
        />
        <YAxis
          width={40}
          tick={{ stroke: '#828282', strokeWidth: 0.25 }}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickCount={5}
          allowDecimals={false}
          tickFormatter={(v: number) => v.toLocaleString()}
        />
        <ChartTooltip
          isAnimationActive={false}
          cursor={false}
          wrapperStyle={{ pointerEvents: 'none' }}
          content={
            <ChartTooltipContent
              className="w-[160px]"
              labelFormatter={(value) => formatFullDate(String(value))}
              indicator="dot"
            />
          }
        />
        <Area
          type="monotone"
          dataKey="pageviews"
          stroke="var(--color-pageviews)"
          strokeWidth={2}
          fill="url(#pageviewsFill)"
          dot={false}
          activeDot={{ r: 3 }}
        />
      </AreaChart>
    </ChartContainer>
  );
}

export function BandwidthBarChart({ data, gridStroke }: { data: BandwidthPoint[]; gridStroke: string }) {
  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
      <BarChart data={data} margin={{ left: 8, right: 8 }} barCategoryGap={'20%'}>
        <CartesianGrid vertical={false} stroke={gridStroke} />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={32}
          tickFormatter={formatDateTick}
        />
        <YAxis
          width={64}
          tick={{ stroke: '#828282', strokeWidth: 0.25 }}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickCount={5}
          allowDecimals={false}
          domain={[0, 'auto']}
          tickFormatter={(v: number) => formatBytes(v)}
        />
        <ChartTooltip
          isAnimationActive={false}
          cursor={false}
          wrapperStyle={{ pointerEvents: 'none' }}
          content={
            <ChartTooltipContent
              className="w-[160px]"
              labelFormatter={(value) => formatFullDate(String(value))}
              formatter={(value) => (
                <div className="flex w-full items-center justify-between gap-2">
                  <span className="text-bolt-ds-textSecondary">Bandwidth</span>
                  <span className="font-mono font-medium tabular-nums text-bolt-ds-textPrimary">
                    {formatBytes(Number(value))}
                  </span>
                </div>
              )}
              indicator="dot"
            />
          }
        />
        <Bar dataKey="bandwidth" fill="var(--color-bandwidth)" barSize={18} maxBarSize={24} />
      </BarChart>
    </ChartContainer>
  );
}
